import { useNavigate } from 'react-router-dom';

interface HistoryStateWithIndex {
  idx?: number;
}

function getReferrerPathname(): string | null {
  if (!document.referrer) {
    return null;
  }

  try {
    const referrerUrl = new URL(document.referrer);
    return referrerUrl.origin === window.location.origin ? referrerUrl.pathname : null;
  } catch {
    return null;
  }
}

/**
 * Hook for handling back navigation with fallback.
 *
 * Uses browser history when available, otherwise falls back to a default route.
 * This is useful for pages that can be accessed directly via URL (no history)
 * or via navigation from another page.
 *
 * @param defaultRoute - Fallback route when no browser history exists
 * @returns Function to trigger back navigation
 *
 * @example
 * const goBack = useBackNavigation('/courses');
 * <SDSIconButton icon={<ArrowLeft />} onClick={goBack} />
 */
export function useBackNavigation(defaultRoute: string): () => void {
  const navigate = useNavigate();

  return () => {
    const currentPathname = window.location.pathname;
    const historyState = window.history.state as HistoryStateWithIndex | null;
    const historyIndex = historyState?.idx ?? 0;
    const hasBrowserHistory = window.history.length > 2;

    // React Router increments idx for in-app navigations. If idx > 0 we know there is
    // meaningful same-document history and can trust browser back.
    if (historyIndex > 0) {
      navigate(-1);
      return;
    }

    const referrerPathname = getReferrerPathname();
    const isOnDefaultRoute = currentPathname === defaultRoute;

    // Initial document loads need stricter handling:
    // - listing pages can still rely on browser history to return to the prior screen
    // - detail/form pages only trust referrers that came from their declared listing route
    // Note: always require hasBrowserHistory to avoid no-op navigate(-1) in new tabs
    if (hasBrowserHistory && (isOnDefaultRoute || referrerPathname === defaultRoute)) {
      navigate(-1);
      return;
    }

    navigate(defaultRoute);
  };
}
